<script setup>
import { useElementSize } from '@vueuse/core';
import { computed, ref } from 'vue';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';
import FeatureActionsToolbar from '~/terra/components/feature-details/actions-toolbar/feature-actions-toolbar.vue';
import FeatureDetails from '~/terra/components/feature-details/feature-details.vue';
import { useArcgisOverlay } from '~/terra/composables/use-arcgis-overlay.composable.js';
import { useTerraStore } from '~/terra/store/terra.store.js';
import { useCommonStore } from '~/common/stores/common.store.js';

const props = defineProps({
  mapStyle: {
    type: String,
    default: '',
  },
  sidebarState: {
    type: Object,
    required: true,
  },
  taskMarkerActive: {
    type: Boolean,
    default: false,
  },
  formMarkerActive: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'handleMapStyleChange',
  'filterGlDrawConvertedFeatures',
  'openCreationPopup',
  'updateTableActive',
]);

const { $t, auth_store } = useCommonImports();
const terra_store = useTerraStore();
const common_store = useCommonStore();

// ArcGIS overlay — driven by container settings
const arcgis_layer = computed(() => terra_store.container?.properties?.arcgis?.layers?.[0]);
const { overlay_layer_active, overlay_token_loading, toggleOverlayLayer } = useArcgisOverlay();

// Container ref and size tracking
const footer_container = ref(null);
const { width: containerWidth } = useElementSize(footer_container);

// Calculate available width for the toolbar
const available_toolbar_width = computed(() => {
  let availableWidth = containerWidth.value;

  if (props.mapStyle)
    availableWidth -= 60; // Width of the satellite/street image + margins

  if (terra_store.selected_features?.length >= 1 && !props.taskMarkerActive && !props.formMarkerActive)
    availableWidth -= 360;

  availableWidth -= 16; // space-x-4 class adds 16px of spacing

  return Math.max(availableWidth, 200);
});

const show_feature_components = computed(() => {
  return Boolean(
    terra_store.selected_features?.length >= 1
    && !props.taskMarkerActive
    && !props.formMarkerActive,
  );
});

// Methods
function handleMapStyleChange() {
  emit('handleMapStyleChange');
}

function filterGlDrawConvertedFeatures(payload) {
  emit('filterGlDrawConvertedFeatures', payload);
}

function openCreationPopup(type) {
  emit('openCreationPopup', type);
}

function updateTableActive() {
  emit('updateTableActive', true);
  terra_store.table_state.should_sort_selected_features = true;
}

function onToggleOverlayLayer() {
  toggleOverlayLayer(terra_store.map);
}
</script>

<template>
  <div>
    <div
      ref="footer_container"
      :style="`${
        Boolean(sidebarState.active_menu.length)
          ? `width:calc(100vw - 400px);`
          : `width:calc(100vw - 100px);`
      }`"
      class="fixed bottom-3 flex justify-between items-end space-x-4 w-auto"
    >
      <!-- Left side: base-style toggle + overlay-layer toggle -->
      <div class="flex items-end gap-2">
        <!-- Original satellite / street style toggle (unchanged behaviour) -->
        <div
          v-if="mapStyle"
          v-tippy="!mapStyle.includes('satellite') ? $t('Satellite') : $t('Street')"
          @click="handleMapStyleChange"
        >
          <img
            v-if="!mapStyle.includes('satellite')"
            src="../assets/satellite.jpg" alt="Satellite" class="cursor-pointer !rounded-lg border-2 border-white"
          >
          <img
            v-else
            src="../assets/street.jpg" alt="Street" class="cursor-pointer !rounded-lg border-2 border-white"
          >
        </div>

        <!-- Overlay layer toggle button -->
        <button
          v-if="!common_store.is_production && terra_store.container?.properties?.arcgis?.enable && (arcgis_layer?.url)"
          v-tippy="overlay_token_loading ? $t('Loading layer…') : overlay_layer_active ? $t('Hide overlay layer') : $t('Show overlay layer')"
          type="button"
          :disabled="overlay_token_loading"
          class="flex w-[3.4rem] h-[3.4rem] flex-col items-center justify-center w-10 h-10 rounded-lg border-2 transition-all duration-150 focus:outline-none"
          :class="overlay_token_loading
            ? 'bg-white/90 border-white text-gray-400 cursor-wait'
            : overlay_layer_active
              ? 'bg-blue-600 border-blue-500 text-white shadow-lg shadow-blue-500/30 cursor-pointer'
              : 'bg-white/90 border-white text-gray-600 hover:border-blue-400 hover:text-blue-500 cursor-pointer'"
          @click="onToggleOverlayLayer"
        >
          <!-- Spinner while token is loading -->
          <icon-hawk-refresh-ccw-five
            v-if="overlay_token_loading"
            class="w-8 h-8 animate-spin text-blue-500"
          />

          <!-- Layers stack icon (shown once token is ready) -->
          <icon-hawk-layers-three-two
            v-else
            class="w-8 h-8"
          />
        </button>
      </div>

      <template v-if="auth_store.check_split('feature_actions_improvements') && show_feature_components">
        <FeatureActionsToolbar
          class="mb-0.5"
          :available-width="available_toolbar_width"
          @open-creation-popup="openCreationPopup"
        />
        <div class="w-[352px] z-[-100] h-1 bg-transparent" />
      </template>
    </div>
    <FeatureDetails
      v-if="show_feature_components"
      class="fixed bottom-3 right-3"
      @filter-gl-draw-converted-features="filterGlDrawConvertedFeatures"
      @is-table-active="updateTableActive"
      @open-creation-popup="openCreationPopup"
    />
  </div>
</template>
